import Modal, { ModalActionData } from '@betterfunnel/ui-core/Modal';
import TextArea from '@betterfunnel/ui-core/TextArea';
import { DialogTitle } from '@headlessui/react';
import * as Sentry from '@sentry/react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DashboardContext } from '../contexts/DashboardContext';
import { DynamicPageContext } from '../contexts/DynamicPageContext';
import { UserContext } from '../contexts/UserContext';
import { updateWebsite } from '../services/WebsitesService';

interface Props {
  testId?: string;
  open: boolean;
  onClose: () => void;
}

export default function EditTestDescription({ testId, open, onClose }: Props) {
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage } = useContext(DashboardContext);
  const { websites } = useContext(DynamicPageContext);

  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error>();

  const website = useMemo(
    () => (testId ? websites?.find((v) => v.id === testId) : undefined),
    [testId, websites],
  );

  useEffect(() => {
    setName(website?.data().name || '');
  }, [website]);

  useEffect(() => {
    if (open) return;

    setSaving(false);
    setError(undefined);
  }, [open]);

  const saveNameAndDescription = useCallback(async () => {
    if (saving) return;
    if (!website) return;
    if (!selectedAccount || !selectedDynamicPage) return;

    setSaving(true);

    try {
      const update = {
        name,
      };
      await updateWebsite(
        selectedAccount.id,
        selectedDynamicPage.id,
        website.id,
        update,
      );
      onClose();
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e);
    }
    setSaving(false);
  }, [saving, website, selectedAccount, selectedDynamicPage, name, onClose]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: saving,
        onClick: () => onClose(),
      },
      {
        title: 'Update',
        type: 'primary',
        loading: saving,
        onClick: () => saveNameAndDescription(),
      },
    ],
    [saving, onClose, saveNameAndDescription],
  );

  return (
    <Modal open={open} onClose={onClose} actions={actions} width="lg">
      <DialogTitle
        as="h3"
        className="text-base font-semibold leading-6 text-gray-900"
      >
        Update test description
      </DialogTitle>
      <div className="mt-4 flex flex-col gap-4">
        <TextArea
          title="Description"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      {error ? (
        <div className="mt-5 w-full text-end text-red-600">
          {error.message || 'Failed to create workspace'}
        </div>
      ) : (
        <></>
      )}
    </Modal>
  );
}
